import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../shared/DashboardLayout';
// Phase 4 Components
import PatientList from '../patients/PatientList';
import './PrescriptorsDashboard.css';

/**
 * Prescriptors Dashboard
 * Medical staff with patient-specific protocol permissions
 */
const PrescriptorsDashboard = ({ onLogout, userRole, userData, dashboardConfig }) => {
  const { t } = useTranslation('dashboards');
  const [activeSection, setActiveSection] = useState('my-patients');
  const [userName, setUserName] = useState('');

  useEffect(() => {
    if (userData) {
      setUserName(userData.full_name || `${userData.first_name} ${userData.last_name}`);
    }
  }, [userData]);

  const [expandedSections, setExpandedSections] = React.useState({
    'patients-section': true,
    'protocols-section': false,
    'tasks-section': false
  });

  const navigationItems = [
    {
      id: 'patients-section',
      label: t('prescriptors.myPatients'),
      type: 'section',
      expanded: expandedSections['patients-section'],
      children: [
        { id: 'my-patients', label: t('prescriptors.activePatients') },
        { id: 'patient-protocols', label: t('prescriptors.patientProtocols') },
        { id: 'risk-assessments', label: t('prescriptors.riskAssessments') }
      ]
    },
    {
      id: 'protocols-section',
      label: t('prescriptors.protocols'),
      type: 'section',
      expanded: expandedSections['protocols-section'],
      children: [
        { id: 'input-protocols', label: t('prescriptors.inputPatientProtocols') },
        { id: 'review-protocols', label: t('prescriptors.reviewProtocols') }
      ]
    },
    {
      id: 'tasks-section',
      label: t('prescriptors.taskManagement'),
      type: 'section',
      expanded: expandedSections['tasks-section'],
      children: [
        { id: 'create-patient-tasks', label: t('prescriptors.createPatientTasks') },
        { id: 'monitor-tasks', label: t('prescriptors.monitorTasks') }
      ]
    },
    { type: 'divider' },
    { id: 'patient-compliance', label: t('prescriptors.patientCompliance'), type: 'item' },
    { id: 'patient-analytics', label: t('prescriptors.patientAnalytics'), type: 'item' },
    { id: 'education', label: t('prescriptors.medicalEducation'), type: 'item' }
  ];

  const handleNavigate = (sectionId) => {
    if (sectionId.startsWith('toggle-')) {
      const section = sectionId.replace('toggle-', '');
      setExpandedSections(prev => ({
        ...prev,
        [section]: !prev[section]
      }));
      return;
    }
    setActiveSection(sectionId);
  };

  const renderContent = () => {
    switch (activeSection) {
      // Phase 4: Patient Management - Show patients in prescriptor's department
      case 'my-patients':
        return <PatientList departmentFilter={userData?.primary_department_id} />;
      
      case 'patient-protocols':
        return (
          <div className="prescriptor-content">
            <h2>{t('prescriptors.patientProtocols')}</h2>
            <p>{t('prescriptors.patientProtocolsDesc')}</p>
            <div className="coming-soon">
              <span className="icon">📋</span>
              <p>{t('comingSoon')}</p>
            </div>
          </div>
        );
      
      case 'risk-assessments':
        return (
          <div className="prescriptor-content">
            <h2>{t('prescriptors.riskAssessments')}</h2>
            <p>{t('prescriptors.riskAssessmentsDesc')}</p>
            <div className="coming-soon">
              <span className="icon">⚠️</span>
              <p>{t('comingSoon')}</p>
            </div>
          </div>
        );
      
      case 'input-protocols':
        return (
          <div className="prescriptor-content">
            <h2>{t('prescriptors.inputPatientProtocols')}</h2>
            <p>{t('prescriptors.inputPatientProtocolsDesc')}</p>
            <div className="coming-soon">
              <span className="icon">➕</span>
              <p>{t('comingSoon')}</p>
            </div>
          </div>
        );
      
      case 'review-protocols':
        return (
          <div className="prescriptor-content">
            <h2>{t('prescriptors.reviewProtocols')}</h2>
            <p>{t('prescriptors.reviewProtocolsDesc')}</p>
            <div className="coming-soon">
              <span className="icon">👁️</span>
              <p>{t('comingSoon')}</p>
            </div>
          </div>
        );
      
      case 'create-patient-tasks':
        return (
          <div className="prescriptor-content">
            <h2>{t('prescriptors.createPatientTasks')}</h2>
            <p>{t('prescriptors.createPatientTasksDesc')}</p>
            <div className="coming-soon">
              <span className="icon">➕</span>
              <p>{t('comingSoon')}</p>
            </div>
          </div>
        );
      
      case 'monitor-tasks':
        return (
          <div className="prescriptor-content">
            <h2>{t('prescriptors.monitorTasks')}</h2>
            <p>{t('prescriptors.monitorTasksDesc')}</p>
            <div className="coming-soon">
              <span className="icon">📊</span>
              <p>{t('comingSoon')}</p>
            </div>
          </div>
        );
      
      case 'patient-compliance':
        return (
          <div className="prescriptor-content">
            <h2>{t('prescriptors.patientCompliance')}</h2>
            <p>{t('prescriptors.patientComplianceDesc')}</p>
            <div className="coming-soon">
              <span className="icon">✓</span>
              <p>{t('comingSoon')}</p>
            </div>
          </div>
        );
      
      case 'patient-analytics':
        return (
          <div className="prescriptor-content">
            <h2>{t('prescriptors.patientAnalytics')}</h2>
            <p>{t('prescriptors.patientAnalyticsDesc')}</p>
            <div className="coming-soon">
              <span className="icon">📈</span>
              <p>{t('comingSoon')}</p>
            </div>
          </div>
        );
      
      case 'education':
        return (
          <div className="prescriptor-content">
            <h2>{t('prescriptors.medicalEducation')}</h2>
            <p>{t('prescriptors.medicalEducationDesc')}</p>
            <div className="coming-soon">
              <span className="icon">🎓</span>
              <p>{t('comingSoon')}</p>
            </div>
          </div>
        );
      
      default:
        return (
          <div className="prescriptor-content">
            <h2>{t('prescriptors.title')}</h2>
            <p>{t('prescriptors.welcome')}</p>
          </div>
        );
    }
  };

  return (
    <DashboardLayout
      userRole={userRole}
      userName={userName}
      onLogout={onLogout}
      navigationItems={navigationItems}
      activeSection={activeSection}
      onNavigate={handleNavigate}
      showNotifications={true}
    >
      {renderContent()}
    </DashboardLayout>
  );
};

export default PrescriptorsDashboard;

